import React, { memo, PropsWithChildren } from 'react';
import { Button } from 'components/ui/button';
import { Switch } from 'components/ui/switch';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from 'components/ui/collapsible';
import { cn } from 'lib/utils';

interface Props extends PropsWithChildren {
  onChangeSwitch: (expanded: boolean) => void;
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  expanded: boolean;
  title: string;
  description?: string;
  disabled?: boolean;
  loading?: boolean;
  disableSwitcher?: boolean;
  switchDisabled?: boolean;
  submitLabel?: string;
  disableSubmitLabel?: boolean;
  pending?: boolean;
  autoComplete?: 'on' | 'off';
  viewHead?: boolean;
  nullablePadding?: boolean;
}

function ExpandableForm(props: Props) {
  const {
    title,
    expanded,
    onChangeSwitch,
    onSubmit,
    description,
    disabled = false,
    loading = false,
    disableSwitcher = false,
    switchDisabled = false,
    submitLabel = 'Создать',
    children,
    autoComplete = 'on',
    viewHead = true,
    nullablePadding = false,
    disableSubmitLabel = false,
    pending = false,
  } = props;

  return (
    <form onSubmit={onSubmit} autoComplete={autoComplete}>
      <Collapsible open={expanded} onOpenChange={onChangeSwitch}>
        {viewHead && (
          <div className="flex items-center justify-between gap-4 pb-2">
            <div className="flex flex-col flex-grow min-w-0">
              <p className="font-medium text-lg sm:text-2xl leading-8 text-[#0a0a0a] truncate m-0">
                {expanded ? title : description || title}
              </p>
            </div>
            {!disableSwitcher && (
              <Switch
                disabled={switchDisabled}
                checked={expanded}
                onCheckedChange={(checked: boolean) => onChangeSwitch(checked)}
              />
            )}
          </div>
        )}
        <CollapsibleContent
          className={cn(
            nullablePadding ? 'p-0' : 'p-4',
            'transition-all data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up'
          )}
        >
          <div>{children}</div>
        </CollapsibleContent>
      </Collapsible>
      {!disableSubmitLabel && !disabled && typeof onSubmit === 'function' && (
        <div className="pt-4 flex justify-end">
          <Button
            disabled={loading || Boolean(pending)}
            type="submit"
            className="min-w-[120px] flex items-center justify-center"
          >
            {(loading || Boolean(pending)) && (
              <span className="mr-2 inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin" />
            )}
            {submitLabel}
          </Button>
        </div>
      )}
    </form>
  );
}

export default memo(ExpandableForm);
